import React, { type ReactNode, useMemo, useEffect, useCallback } from 'react';
import { styled } from '@compiled/react';
import isEqual from 'lodash/isEqual';
import Button from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InfoIcon from '@atlaskit/icon/glyph/info';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import CardIcon from '../../../common/assets/Cards.svg';
import MembersIcon from '../../../common/assets/Members.svg';
import type { SupportedThirdParty } from '../../../common/constants/import-type';
import { StatisticsType } from '../../../common/constants/steps';
import type { RequestAccessTokenMethod, ThirdPartyEntityStats } from '../../../common/types';
import { useContentForThirdParty } from '../../../common/utils';
import { useThirdPartyState } from '../../../controllers/import-state';
import { useThirdPartyEntityStats } from '../../../services/third-party-entity-stats';
import { useThirdPartyMultiEntityStats } from '../../../services/third-party-multi-entity-stats';
import { Card } from './card';
import messages from './messages';

export type Props = ThirdPartyEntityStats & {
	thirdParty: SupportedThirdParty;
	stats: StatisticsType[];
	removeBackground: boolean;
	requestAuthenticationToken: RequestAccessTokenMethod;
	isMultipleEntitiesSupported: boolean;
};

export const EntityStats = ({
	thirdParty,
	stats,
	removeBackground,
	requestAuthenticationToken,
	isMultipleEntitiesSupported,
}: Props) => {
	const { formatMessage } = useIntl();
	const { statsHeading, cardStats, memberStats, cardStatsInfo } =
		useContentForThirdParty(thirdParty);

	const {
		fetchThirdPartyEntityStats,
		hasErrorOccurred: hasEntityStatsErrorOccurred,
		isLoading: isEntityStatsLoading,
	} = useThirdPartyEntityStats({
		thirdParty,
		requestAuthenticationToken,
	});

	const {
		fetchThirdPartyMultiEntityStats,
		hasErrorOccurred: hasMultiEntityStatsErrorOccurred,
		isLoading: isMultiEntityStatsLoading,
	} = useThirdPartyMultiEntityStats({
		thirdParty,
		requestAuthenticationToken,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [
		{ selectedEntityId, selectedEntityName, entityStats, selectedEntities, selectedEntity },
		{ setThirdPartyState },
	] = useThirdPartyState();

	const prevSelectedEntities = usePrevious(selectedEntities);

	const fetchMultiEntityStats = useCallback(() => {
		const entityIds = selectedEntities?.map((entity) => entity.id) ?? [];
		if (
			entityIds.length === 0 ||
			isEqual(selectedEntities, prevSelectedEntities) ||
			entityStats !== null
		) {
			return;
		}
		fetchThirdPartyMultiEntityStats(entityIds).then((statistics) => {
			setThirdPartyState({ entityStats: statistics });
			fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyMultiEntityStats retrieved', {
				thirdParty,
				members: statistics?.members || 0,
				tasks: (fg('jwm_clickup_importer') ? statistics?.tasksCount : statistics?.tasks) || 0,
			});
		});
	}, [
		createAnalyticsEvent,
		entityStats,
		fetchThirdPartyMultiEntityStats,
		prevSelectedEntities,
		selectedEntities,
		setThirdPartyState,
		thirdParty,
	]);

	const fetchStats = useCallback(() => {
		if (isMultipleEntitiesSupported) {
			fetchMultiEntityStats();
		} else {
			if (fg('jira_importer_third_party_state_refactor')) {
				if (selectedEntityId === null || selectedEntity === null || entityStats !== null) {
					return;
				}
			} else if (selectedEntityId === null || selectedEntityName === null || entityStats !== null) {
				return;
			}
			fetchThirdPartyEntityStats(selectedEntityId).then((statistics) => {
				setThirdPartyState({ entityStats: statistics });
				fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyEntityStats retrieved', {
					thirdParty,
					members: statistics?.members || 0,
					tasks: (fg('jwm_clickup_importer') ? statistics?.tasksCount : statistics?.tasks) || 0,
				});
			});
		}
		// ommit isMultipleEntitiesSupported which cause multiple fetches
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		createAnalyticsEvent,
		entityStats,
		fetchThirdPartyEntityStats,
		selectedEntityId,
		selectedEntityName,
		selectedEntities,
		setThirdPartyState,
		thirdParty,
	]);

	const onRetryFetchEntityStats = useCallback(() => {
		fetchStats();
	}, [fetchStats]);

	useEffect(() => {
		fetchStats();
	}, [fetchStats]);

	const { members, tasks, tasksCount } = entityStats || {};
	const numOfTasks = fg('jwm_clickup_importer') ? tasksCount : tasks;
	const statsMap = useMemo(
		() => ({
			[StatisticsType.TASKS]: (
				<Card
					key={StatisticsType.TASKS}
					cardContent={cardStats}
					statistic={numOfTasks}
					showLoadingState={
						isEntityStatsLoading || (isMultipleEntitiesSupported && isMultiEntityStatsLoading)
					}
					icon={
						<IconWrapper
							backgroundColor={token('color.background.accent.blue.subtler', colors.B50)}
						>
							<Icon
								src={CardIcon}
								aria-label="cards"
								role="img"
								alt=""
								data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.entity-stats.cards-icon"
							/>
						</IconWrapper>
					}
				/>
			),
			[StatisticsType.MEMBERS]: (
				<Card
					key={StatisticsType.MEMBERS}
					cardContent={memberStats}
					statistic={members}
					showLoadingState={
						isEntityStatsLoading || (isMultipleEntitiesSupported && isMultiEntityStatsLoading)
					}
					icon={
						<IconWrapper
							backgroundColor={token('color.background.accent.gray.subtlest', colors.N20)}
						>
							<Icon
								src={MembersIcon}
								aria-label="members"
								role="img"
								alt=""
								data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.entity-stats.members-icon"
							/>
						</IconWrapper>
					}
				/>
			),
		}),
		[
			cardStats,
			numOfTasks,
			isEntityStatsLoading,
			isMultipleEntitiesSupported,
			isMultiEntityStatsLoading,
			memberStats,
			members,
		],
	);

	return (
		<>
			<Wrapper
				data-testid="project-create-importer-upload-flow.ui.select-third-party-entity-screen.entity-stats.wrapper"
				removeBackground={removeBackground}
			>
				<Header>{statsHeading}</Header>
				{(hasEntityStatsErrorOccurred ||
					(isMultipleEntitiesSupported && hasMultiEntityStatsErrorOccurred)) && (
					<ErrorMessageWrapper>
						<ErrorIcon label="" primaryColor={token('color.icon.danger', colors.R400)} />
						<ErrorMessage>
							{formatMessage(messages.errorOccurredWithLink, {
								link: (chunks: ReactNode) => (
									<Button onClick={onRetryFetchEntityStats} appearance="link" spacing="none">
										{chunks}
									</Button>
								),
							})}
						</ErrorMessage>
					</ErrorMessageWrapper>
				)}
				{stats.map((stat) => statsMap[stat])}
			</Wrapper>
			{stats.includes(StatisticsType.MEMBERS) && members && (
				<UserMessageWrapper>
					<InfoIcon label="" primaryColor={token('color.icon.subtle', colors.N300)} />
					<UserMessageDescription>
						{formatMessage(messages.entityStatsUserMessage)}
					</UserMessageDescription>
				</UserMessageWrapper>
			)}
			{cardStatsInfo && (
				<Inline space="space.050" alignBlock="center">
					<InfoIcon label="" primaryColor={token('color.icon.subtle', colors.N300)} />
					<Box xcss={messageStyles}>{formatMessage(cardStatsInfo)}</Box>
				</Inline>
			)}
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{ removeBackground?: boolean }>({
	width: '100%',
	borderRadius: '16px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ removeBackground }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		removeBackground ? 'transparent' : token('elevation.surface.sunken', colors.N10),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	padding: ({ removeBackground }) =>
		removeBackground ? `${token('space.300', '24px')} 0` : token('space.300', '24px'),
	boxSizing: 'border-box',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.h2({
	fontWeight: '600',
	fontSize: '14px',
	lineHeight: '16px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N200),
});

type IconWrapperProps = { backgroundColor: string };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div<IconWrapperProps>({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '112px',
	minWidth: '112px',
	height: '112px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: `${(props: IconWrapperProps) => props.backgroundColor}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled.img({
	width: '80%',
	height: '56px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorMessageWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	maxWidth: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorMessage = styled.span({
	overflowWrap: 'break-word',
	width: '90%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserMessageWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	padding: token('space.150', '12px'),
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserMessageDescription = styled.span({
	marginLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle', colors.N300),
	fontSize: '12px',
});

const messageStyles = xcss({
	fontSize: '12px',
});
